<script>
  import {
    UsersRepository,
    GroupsRepository,
    RolesRepository,
  } from "../repositories/index";
  import { CustomAutocomplete } from "./index";
  import { validators } from "../utils";
  import { user } from "../stores/index";
  import { addDays, format, startOfDay } from "date-fns";
  import {
    Button,
    Form,
    TextField,
    Submit,
    Flex,
    FlexItem,
    Autocomplete,
    Checkbox,
    Modal,
    Flatpickr,
  } from "@gl/design-system";
  import { _, language } from "../stores/index";

  import { createEventDispatcher, onMount } from "svelte";
  import { ModalType } from "../types/modal";
  const dispatch = createEventDispatcher();

  export let groups;
  export let roles;
  export let modalType;
  export let data;
  export let active;

  const loading = {};

  const form = {
    groups: [],
    roles: [],
  };

  async function validateEmail(value) {
    if (
      !value &&
      (modalType === ModalType.ServiceAccountPost ||
        modalType === ModalType.ServiceAccountPut)
    )
      return null;

    const formatError = validators.isEmail(value);
    if (formatError[0] === false) return formatError;
    if (modalType === ModalType.Post) {
      try {
        await UsersRepository.addUser({ email: value }, true);
      } catch (e) {
        if (e?.response?.data?.message) return [false, e.response.data.message];
      }
    }
  }

  let autocompleteGroups;
  let autocompleteRoles;

  async function onSubmit() {
    loading.submit = true;

    let requestBody = {
      firstName:
        modalType === ModalType.ServiceAccountPut ? undefined : form.firstName,
      lastName:
        modalType !== ModalType.ServiceAccountPut ? form.lastName : undefined,
      isMaster: form.master,
      isServiceAccount:
        modalType === ModalType.ServiceAccountPut ||
        modalType === ModalType.ServiceAccountPost,
      expiration:
        form.hasExpiration && form.expiration
          ? new Date(form.expiration).toISOString()
          : null,
    };
    if (
      modalType !== ModalType.Put &&
      (modalType !== ModalType.ServiceAccountPost || form.email)
    ) {
      requestBody.email = form.email;
    }

    let user;
    try {
      user =
        modalType === ModalType.Put || modalType === ModalType.ServiceAccountPut
          ? await UsersRepository.editUser(data.id, requestBody)
          : await UsersRepository.addUser(requestBody);
    } finally {
      if (user) {
        //add groups to user
        if (
          form.groups &&
          (modalType === ModalType.Post ||
            modalType === ModalType.ServiceAccountPost)
        ) {
          await Promise.all(
            form.groups.map((g) =>
              GroupsRepository.addGroupUser(g.value, {
                users: [user.id],
              }),
            ),
          );
        }
        if (
          form.roles &&
          (modalType === ModalType.Post ||
            modalType === ModalType.ServiceAccountPost)
        ) {
          await Promise.all(
            form.roles.map((g) =>
              RolesRepository.addRoleUser(g.value, {
                users: [user.id],
              }),
            ),
          );
        }
        dispatch("success", { userId: user.userId });
        dispatch("close");
      }
      loading.submit = false;
    }
  }

  onMount(() => {
    if (
      modalType === ModalType.Put ||
      modalType === ModalType.ServiceAccountPut
    ) {
      form.firstName = data.firstName;
      form.lastName = data.lastName;
      form.email = data.email;
      form.master = data.isMaster;
      if (data.expiration) form.hasExpiration = true;
      form.expiration =
        data.expiration && format(new Date(data.expiration), "yyyy-MM-dd");
    } else {
      autocompleteGroups =
        groups.length &&
        groups.map(
          (group) =>
            (group = {
              label: group.name,
              value: group.id,
              disabled: !!group.missingPolicies,
            }),
        );
      autocompleteRoles =
        roles.length &&
        roles.map(
          (role) =>
            (role = {
              label: role.name,
              value: role.id,
              disabled: !!role.missingPolicies,
            }),
        );
      form.expiration = new Date().toISOString();
    }
  });
</script>

<Modal bind:active on:close boxed>
  <div class="gl-title mb-4">
    {#if modalType === ModalType.ServiceAccountPut}
      {$_("users.service_account.edit")}
    {:else if modalType === ModalType.ServiceAccountPost}
      {$_("users.service_account.new")}
    {:else if modalType === ModalType.Put}
      {$_("users.edit_user")}
    {:else}
      {$_("users.add_user")}
    {/if}
  </div>

  <Form validationMode="eager">
    <TextField
      bind:value={form.email}
      id="user-email"
      label={$_("users.email_address")}
      variant="Email"
      required={modalType === ModalType.Post
        ? $_("validations.required")
        : false}
      validate={validateEmail}
      disabled={modalType === ModalType.Put}
    />

    {#if modalType === ModalType.ServiceAccountPut || modalType === ModalType.ServiceAccountPost}
      <TextField
        bind:value={form.firstName}
        id="user-firstName"
        label={$_("common.name")}
        required={$_("validations.required")}
        validate={(value) => validators.isValidNameWithSpacesAndNumbers(value)}
        disabled={modalType === ModalType.ServiceAccountPut}
        maxLength={50}
        config={{ characterCount: $_("character.count") }}
      />
    {:else}
      <TextField
        bind:value={form.firstName}
        id="user-firstName"
        label={$_("users.firstName")}
        required={$_("validations.required")}
        validate={(value) => validators.isValidNameWithSpaces(value)}
        maxLength={50}
        config={{ characterCount: $_("character.count") }}
      />

      <TextField
        bind:value={form.lastName}
        id="user-lastName"
        label={$_("users.lastName")}
        required={$_("validations.required")}
        validate={(value) => validators.isValidNameWithSpaces(value)}
        maxLength={50}
        config={{ characterCount: $_("character.count") }}
      />{/if}

    {#if autocompleteGroups && (modalType === ModalType.Post || modalType === ModalType.ServiceAccountPost)}
      <div class="mb-5">
        <p class="mb-2">{$_("common.groups")}</p>
        <CustomAutocomplete
          options={autocompleteGroups}
          bind:value={form.groups}
        />
      </div>
    {/if}

    {#if autocompleteRoles && (modalType === ModalType.Post || modalType === ModalType.ServiceAccountPost)}
      <div class="mb-5">
        <p class="mb-2">{$_("common.roles")}</p>
        <CustomAutocomplete
          options={autocompleteRoles}
          bind:value={form.roles}
        />
      </div>
    {/if}

    {#if $user.data.isMaster}
      <Checkbox bind:value={form.master} label={$_("user.master")} />
    {/if}

    <Flex class="align-items-center mb-4">
      <div class="mb-0">
        <Checkbox
          bind:value={form.hasExpiration}
          label={$_("account.expiration.set")}
          on:change={(e) => {
            if (e.target.checked)
              form.expiration = format(addDays(new Date(), 1), "yyyy-MM-dd");
          }}
        />
      </div>

      {#if form.hasExpiration}
        <FlexItem class="ml-3">
          <Flatpickr
            bind:value={form.expiration}
            format="d.m.Y"
            placeholder="DD.MM.YYYY"
            language={$language}
            showTime={false}
            minDate={startOfDay(addDays(new Date(), 1)).toISOString()}
          />
        </FlexItem>
      {/if}
    </Flex>

    {#if modalType === ModalType.Put || modalType === ModalType.ServiceAccountPut}
      <p class="color-text mb-5" style="font-size:14px">
        {$_("users.edit.warning")}
      </p>
    {/if}

    <Flex>
      <FlexItem>
        <Submit
          loading={loading.submit}
          on:submit={onSubmit}
          label={$_("buttons.save")}
        />
      </FlexItem>
      <FlexItem class="ml-4">
        <Button outlined on:click={() => dispatch("close")}
          >{$_("buttons.cancel")}</Button
        >
      </FlexItem>
    </Flex>
  </Form>
</Modal>
